/**
 * CxL3 — automatic transfer switches and changeover panels.
 *
 * Two things carry the weight here and both are easy to sign off without having
 * proved anything:
 *
 *  - **The timers.** An ATS that transfers is not an ATS that transfers *at the
 *    設定 times*. Each delay is measured, not observed, because the whole point of
 *    the sequence is that it interlocks with the generator's start time.
 *  - **The interlock.** The test that matters is the one that proves both sources
 *    can never be closed together. It is recorded as its own check so it cannot
 *    be lost inside a general "transfer works" line.
 *
 * Draft defaults. Replace with the CxA's issued script when it arrives.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_ATS: readonly CatalogueTest[] = [
  {
    code: 'ATS-VIS',
    name: 'Visual inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Visual Inspection',
    checks: [
      { label: 'Rating plate and source labelling match the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Both source cables terminated, torqued and labelled', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Neutral arrangement (switched or solid) as designed', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Earthing and bonding tight and labelled', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Operating instructions and manual-transfer procedure displayed', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Controller settings recorded against the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['ATS-VIS-PLATE', 'ATS-VIS-CABLE', 'ATS-VIS-SETTINGS'],
  },
  {
    code: 'ATS-VT',
    name: 'Source measurements and rotation',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Voltage Test',
    checks: [
      { label: 'Normal source L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Normal source L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Normal source L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Emergency source L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Emergency source L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Emergency source L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Both sources have the same phase rotation', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Identical rotation — a mismatch is a stop', requiresPhoto: true },
      { label: 'Load-side voltage after transfer', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
    ],
    shotCodes: ['ATS-PR-NORMAL', 'ATS-PR-EMERG', 'ATS-VT-LOAD'],
  },
  {
    code: 'ATS-TRANSFER',
    name: 'Automatic transfer and retransfer',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Simulated by opening the normal source upstream, not by pressing a test button, wherever the site allows it.',
    checks: [
      { label: 'Normal source failure detected', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Controller senses loss and begins the start sequence' },
      { label: 'Generator start signal issued', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Start contact closes; the set cranks' },
      { label: 'Transfer to emergency source', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Load transfers once the emergency source is within its window' },
      { label: 'Normal source restored and accepted', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Controller accepts the source after its stability delay' },
      { label: 'Retransfer to normal source', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Load returns to normal source' },
      { label: 'Generator cool-down and stop', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Set runs the cool-down period, then stops; start contact released' },
      { label: 'Load re-energised after transfer and after retransfer', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['ATS-XFER-NORMAL', 'ATS-XFER-EMERG', 'ATS-XFER-RETURN'],
  },
  {
    code: 'ATS-TIMERS',
    name: 'Timer verification',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Each delay timed against the approved setting. Record the setting and the measured value separately.',
    checks: [
      { label: 'Start delay — normal fail to generator start signal', responseType: 'measured_pass_fail', unit: 's', tolerance: 10, toleranceMode: 'percent' },
      { label: 'Transfer delay — emergency available to transfer', responseType: 'measured_pass_fail', unit: 's', tolerance: 10, toleranceMode: 'percent' },
      { label: 'Retransfer delay — normal stable to return', responseType: 'measured_pass_fail', unit: 's', tolerance: 10, toleranceMode: 'percent' },
      { label: 'Cool-down delay — retransfer to generator stop', responseType: 'measured_pass_fail', unit: 's', tolerance: 10, toleranceMode: 'percent' },
      { label: 'Neutral / centre-off dwell time where fitted', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['ATS-TIMER-SETTINGS'],
  },
  {
    code: 'ATS-INTERLOCK',
    name: 'Interlocking and manual operation',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Interlocking Checks',
    note: 'The no-parallel interlock is the one check here that cannot be waived.',
    checks: [
      { label: 'Both sources cannot be closed together — mechanical interlock', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Second source physically refuses to close while the first is closed', requiresPhoto: true },
      { label: 'Electrical interlock proves the same', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Close command refused' },
      { label: 'Manual transfer following the displayed procedure', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Transfer completes safely under manual control' },
      { label: 'Maintenance bypass / isolation where fitted', responseType: 'yes_no_applicable', toleranceMode: 'none' },
      { label: 'AUTO / MANUAL selector returned to AUTO on completion', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['ATS-INTERLOCK', 'ATS-SELECTOR-AUTO'],
  },
  {
    code: 'ATS-ALARM',
    name: 'EPMS / BMS points',
    cxLevel: 'CxL3',
    equipmentTypes: ['ATS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Check with EPMS/BMS',
    checks: [
      { label: 'Source in use (normal / emergency) point', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Normal source available point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Emergency source available point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'AUTO / MANUAL mode point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Transfer failure / common alarm point', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['ATS-BMS-SCREEN'],
  },
];
